import { ActionItem } from '../components/StepActions';
import { getStepActionSchema } from '../config/stepActionSchemas';

export interface SavedActionState {
  id: number;
  completed?: boolean;
  description?: string;
}

export interface MergeStepActionsResult {
  actions: ActionItem[];
  actionSchemaVersion: number;
  migrationApplied: boolean;
}

function normalizeSavedActions(raw: unknown): SavedActionState[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a): a is SavedActionState => typeof a === 'object' && a !== null && typeof (a as SavedActionState).id === 'number')
    .map((a) => ({
      id: a.id,
      completed: !!a.completed,
      description: typeof a.description === 'string' ? a.description : undefined,
    }));
}

function applyIdRemap(
  completionById: Map<number, boolean>,
  idRemap: Record<number, number>,
  alsoCompleteOnRemap?: Record<number, number[]>
) {
  const remapped = new Map<number, boolean>();
  completionById.forEach((completed, oldId) => {
    const newId = idRemap[oldId] ?? oldId;
    remapped.set(newId, remapped.get(newId) || completed);
    if (completed && alsoCompleteOnRemap?.[oldId]) {
      alsoCompleteOnRemap[oldId].forEach((id) => remapped.set(id, true));
    }
  });
  return remapped;
}

function runMigrations(
  stepNumber: number,
  savedVersion: number,
  completionById: Map<number, boolean>
): { version: number; completionById: Map<number, boolean>; migrationApplied: boolean } {
  const schema = getStepActionSchema(stepNumber);
  let version = savedVersion;
  let map = new Map(completionById);
  let migrationApplied = false;

  while (version < schema.actionSchemaVersion) {
    const nextVersion = version + 1;
    const migration = schema.migrations[nextVersion];
    if (migration?.idRemap) {
      map = applyIdRemap(map, migration.idRemap, migration.alsoCompleteOnRemap);
    }
    version = nextVersion;
    migrationApplied = true;
  }

  return { version, completionById: map, migrationApplied };
}

/**
 * Merge saved action completion onto canonical step actions.
 * Applies schema migrations and grandfathers completed steps.
 */
export function mergeStepActions(
  stepNumber: number,
  originalActions: ActionItem[],
  savedData: {
    actions?: unknown;
    action_schema_version?: number;
  } | null | undefined,
  stepCompleted: boolean
): MergeStepActionsResult {
  const schema = getStepActionSchema(stepNumber);
  const savedVersion =
    typeof savedData?.action_schema_version === 'number' ? savedData.action_schema_version : 0;

  const savedActions = normalizeSavedActions(savedData?.actions);
  let completionById = new Map<number, boolean>();
  savedActions.forEach((a) => {
    completionById.set(a.id, a.completed ?? false);
  });

  const migrated = runMigrations(stepNumber, savedVersion, completionById);
  completionById = migrated.completionById;

  // Always use canonical original copy (titles/descriptions) so locale switches apply;
  // only completion is taken from saved progress.
  const actions = originalActions.map((original) => {
    const completed = completionById.has(original.id) ? completionById.get(original.id)! : false;
    return {
      ...original,
      completed: stepCompleted ? true : completed,
    };
  });

  return {
    actions,
    actionSchemaVersion: schema.actionSchemaVersion,
    migrationApplied: migrated.migrationApplied || savedVersion === 0,
  };
}

/** Minimal action payload for persistence. */
export function actionsForSave(actions: ActionItem[]): { id: number; completed: boolean }[] {
  return actions.map(({ id, completed }) => ({ id, completed }));
}

export function stepDataWithActions(
  actions: ActionItem[],
  actionSchemaVersion: number,
  extraData?: Record<string, unknown>
): Record<string, unknown> {
  return {
    ...(extraData || {}),
    actions: actionsForSave(actions),
    action_schema_version: actionSchemaVersion,
  };
}

/** Merge actions into existing step data, replacing prior actions/schema fields. */
export function buildStepDataWithActions(
  actions: ActionItem[],
  actionSchemaVersion: number,
  existingData?: Record<string, unknown> | null
): Record<string, unknown> {
  const base = existingData ? { ...existingData } : {};
  delete base.actions;
  delete base.action_schema_version;
  return stepDataWithActions(actions, actionSchemaVersion, base);
}
